import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listMeasures, deleteMeasure } from "@/lib/api";
import { FileText, Trash2, Loader2, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  ingesting: { icon: Loader2, cls: "text-[hsl(var(--accent-blue))]", anim: "animate-spin", label: "Ingesting" },
  ingested: { icon: Clock, cls: "text-[hsl(var(--ink-muted))]", label: "Ingested" },
  analyzing: { icon: Loader2, cls: "text-[hsl(var(--accent-blue))]", anim: "animate-spin", label: "Analyzing" },
  analyzed: { icon: CheckCircle2, cls: "text-[hsl(var(--accent-green))]", label: "Analyzed" },
  error: { icon: AlertCircle, cls: "text-[hsl(var(--stamp-red))]", label: "Error" },
};

export default function MeasuresListPage() {
  const [measures, setMeasures] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const data = await listMeasures();
      setMeasures(data);
    } catch (e) {
      toast.error("Failed to load measures");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const handleDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this measure and all its data?")) return;
    try {
      await deleteMeasure(id);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="measures-page">
      <div className="mb-8">
        <div className="section-label mb-2">Dossier</div>
        <h1 className="hero-title text-3xl sm:text-4xl text-[hsl(var(--ink))]">Ingested measures</h1>
        <p className="text-sm text-[hsl(var(--ink-muted))] mt-2">
          Auto-refreshes every 5s. Click a row to open the full report.
        </p>
      </div>

      {loading && (
        <div className="paper-card p-8 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-[hsl(var(--ink-muted))]" />
          <div className="text-sm text-[hsl(var(--ink-muted))]">Loading…</div>
        </div>
      )}

      {!loading && measures.length === 0 && (
        <div className="paper-card p-10 text-center" data-testid="measures-empty">
          <FileText className="w-8 h-8 mx-auto mb-3 text-[hsl(var(--ink-muted))]" />
          <div className="font-serif text-xl mb-1">No measures yet</div>
          <div className="text-sm text-[hsl(var(--ink-muted))] mb-4">
            Scan a California ballot measure to get started.
          </div>
          <Link to="/" className="pill-btn pill-btn-primary inline-flex" data-testid="measures-empty-cta">
            New scan
          </Link>
        </div>
      )}

      <div className="space-y-2">
        {measures.map((m) => {
          const meta = STATUS_META[m.status] || STATUS_META.ingested;
          const Icon = meta.icon;
          return (
            <Link
              to={`/measures/${m.id}`}
              key={m.id}
              data-testid={`measure-row-${m.id}`}
              className="paper-card rounded-sm p-4 flex items-center justify-between gap-4 hover:border-[hsl(var(--ink))] transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="section-label">{m.jurisdiction || "CA"}</span>
                  {m.election_date && (
                    <span className="text-[11px] text-[hsl(var(--ink-muted))] font-mono">
                      · {m.election_date}
                    </span>
                  )}
                </div>
                <div className="font-serif text-lg font-semibold truncate">
                  {m.title || m.measure_id}
                </div>
                <div className="text-xs text-[hsl(var(--ink-muted))] mt-0.5 truncate font-mono">
                  {m.measure_id}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className={`flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider ${meta.cls}`}>
                  <Icon className={`w-3.5 h-3.5 ${meta.anim || ""}`} />
                  {meta.label}
                </div>
                <button
                  data-testid={`delete-${m.id}`}
                  onClick={(e) => handleDelete(m.id, e)}
                  className="text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--stamp-red))] p-1"
                  aria-label="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
